import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "GivingTuesday for Missionaries: A Simple Plan to Capture the Year's Biggest Giving Day";
const DESCRIPTION =
    "A practical, low-pressure plan for missionaries to use GivingTuesday — the save-the-date message, the day-of ask, and the same-day thank-you — without turning support raising into a marketing campaign.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover6.jpg',
            datePublished: '2026-07-30',
            dateModified: '2026-07-30',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/giving-tuesday-for-missionaries',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Is GivingTuesday only for registered nonprofits?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No. GivingTuesday is a global generosity movement, not a nonprofit-only program, and any missionary can use the day to make a focused ask. The advantage is timing, not eligibility — donors are already primed to give that week, whether the gift is tax-deductible or personal support.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many messages should I send for GivingTuesday?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Three is usually enough: a short save-the-date a week before, the actual ask on the day itself, and a same-day thank-you to everyone who gave. More than that risks feeling like a campaign instead of an update from a real person.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I set a specific dollar goal for GivingTuesday?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — a single, concrete goal (like closing a specific monthly gap or funding one project) gives supporters something to rally around and a clear reason the day matters, rather than a vague general ask.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What if GivingTuesday falls right before I usually send a year-end reminder?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Treat them as two distinct moments with different goals: GivingTuesday for a focused, one-time push toward a specific gap, and the year-end reminder in December for recurring-gift renewals and tax-deadline giving. Space them out so donors don't feel asked twice for the same thing.",
                    },
                },
            ],
        },
    ],
};

export default function GivingTuesdayForMissionaries() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/giving-tuesday-for-missionaries' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-giving-tuesday-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover6.jpg" alt="A missionary writing a short thank-you message on a laptop" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>GivingTuesday — the Tuesday after Thanksgiving in the US, now a global generosity movement — isn't reserved for registered charities. For a missionary, it's one of the only days on the calendar when donors are already thinking about giving before you even reach out. A short, focused plan can turn that mindset into real movement on your support, without it feeling like a fundraising campaign.</p>

                    <h2>Why the day works even for personal support raising</h2>

                    <p>Most of the year, an ask has to create its own urgency. On GivingTuesday, the urgency already exists — donors are seeing the phrase everywhere, and giving is simply on their mind. That means your message can be shorter and more direct than usual, because you're not the one manufacturing the occasion.</p>

                    <h2>Pick one specific goal, not a general update</h2>

                    <p>"Please consider supporting me" competes with every other GivingTuesday message in a donor's inbox. "Help me close the last $400/month gap before I leave in March" doesn't. Choose one concrete, time-bound number — a monthly gap, a one-time project cost, or a departure-date target — and make that the entire message.</p>

                    <h2>The three-message plan</h2>

                    <p><strong>One week before:</strong> a short save-the-date. Two or three sentences: GivingTuesday is coming, here's the specific goal, watch for the message on the day. No ask yet — this is just so the day-of message doesn't arrive cold.</p>

                    <p><strong>The day itself:</strong> the actual ask, sent by whichever channel your supporters actually read — email, text, or social. State the goal, the current gap, and a direct link to give. See <a href="/resources/how-to-ask-for-support-over-text-and-social-media">how to ask for support over text and social media</a> for message templates that don't feel like a sales pitch.</p>

                    <p><strong>Same day, once gifts start coming in:</strong> a short thank-you, even a partial one — "Already halfway to the goal, thank you" — sent before the day ends. Momentum visible in real time encourages people who were on the fence to go ahead and give.</p>

                    <h2>Make the ask a one-tap action</h2>

                    <p>Every extra step between the message and the gift costs you donors. Link straight to your Devotion Fund giving page rather than asking people to reply for details or find you on a general website. The fewer clicks between the ask and the gift, the higher the day's conversion.</p>

                    <h2>What to avoid</h2>

                    <p>Don't turn GivingTuesday into a week-long campaign — the day's power comes from its specificity, and repeated asks across many channels start to feel like pressure rather than an update. Don't guilt-trip supporters who don't give that day either; many will give later in December instead, and a gracious tone on the day protects that possibility. See <a href="/resources/is-it-biblical-to-ask-for-support">is it biblical to ask for support</a> for the broader posture worth keeping toward every ask, GivingTuesday included.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Is GivingTuesday only for registered nonprofits?</strong><br />
                    No — it's a generosity movement anyone can use, including missionaries raising personal support.</p>

                    <p><strong>How many messages should I send for GivingTuesday?</strong><br />
                    Three: a save-the-date a week before, the ask on the day, and a same-day thank-you.</p>

                    <p><strong>Should I set a specific dollar goal for GivingTuesday?</strong><br />
                    Yes — a single concrete goal gives supporters a clear reason the day matters.</p>

                    <p><strong>What if GivingTuesday falls right before my year-end reminder?</strong><br />
                    Treat them as two distinct asks with different goals, spaced apart so donors aren't asked twice for the same thing.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/year-end-giving-reminder-for-missionaries">Year-End Giving: How to Remind Donors Before December 31</a></li>
                        <li><a href="/resources/how-to-ask-for-support-over-text-and-social-media">How to Ask for Support Over Text and Social Media</a></li>
                        <li><a href="/resources/how-to-ask-a-one-time-donor-to-become-a-monthly-supporter">How to Ask a One-Time Donor to Become a Monthly Supporter</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We put this plan together after watching missionaries either skip GivingTuesday entirely or over-message it — this is the middle path that's worked well for the people we've talked to.</p>
                    <p><small><em>Last updated: 2026-07-30.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/giving-tuesday-for-missionaries" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
